import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Compass, LayoutDashboard, User, LogOut, Shield, Menu, Megaphone, BarChart3, Sparkles, Users, Briefcase, Wand2 } from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Subtle account-type meta used across the app (kept minimal — one indicator, not badges everywhere).
export const ACCOUNT_TYPE = {
  business: { label: "Business", Icon: Briefcase, chip: "border-indigo-200 bg-indigo-50 text-indigo-700", dot: "bg-indigo-500" },
  creator: { label: "Creator", Icon: Sparkles, chip: "border-emerald-200 bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  admin: { label: "Admin", Icon: Shield, chip: "border-zinc-200 bg-zinc-100 text-zinc-700", dot: "bg-zinc-500" },
};

function AccountTypeChip({ role }) {
  const t = ACCOUNT_TYPE[role];
  if (!t) return null;
  const { Icon } = t;
  return (
    <span data-testid="account-type-chip" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${t.chip}`}>
      <Icon className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{t.label}</span>
    </span>
  );
}

export function Navbar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const profilePath = user?.role === "business" ? "/business/profile" : "/creator/profile";
  const oppPath = user?.role === "business" ? "/business/opportunities" : "/opportunities";

  return (
    <header className="nf-safe-top sticky top-0 z-50 border-b border-zinc-200/80 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo onClick={() => navigate(user ? "/dashboard" : "/")} />

        <nav className="hidden items-center gap-1 md:flex">
          <Button variant="ghost" data-testid="nav-discover" onClick={() => navigate("/discover")} className="text-zinc-600">
            Discover
          </Button>
          {user && user.role !== "admin" && (
            <Button variant="ghost" data-testid="nav-opportunities" onClick={() => navigate(oppPath)} className="text-zinc-600">
              Opportunities
            </Button>
          )}
          {user && (
            <Button variant="ghost" data-testid="nav-dashboard" onClick={() => navigate("/dashboard")} className="text-zinc-600">
              Dashboard
            </Button>
          )}
          {user && user.role === "creator" && (
            <Button variant="ghost" data-testid="nav-network" onClick={() => navigate("/network")} className="text-zinc-600">
              Network
            </Button>
          )}
          {user && user.role === "business" && (
            <Button variant="ghost" data-testid="nav-marketing" onClick={() => navigate("/business/marketing")} className="text-zinc-600">
              AI Marketing
            </Button>
          )}
          {user && user.role !== "admin" && (
            <Button variant="ghost" data-testid="nav-analytics" onClick={() => navigate("/analytics")} className="text-zinc-600">
              Analytics
            </Button>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {!user ? (
            <>
              <Button variant="outline" data-testid="nav-login" onClick={() => navigate("/auth?mode=login")} className="rounded-full border-zinc-300">
                Log in
              </Button>
              <Button data-testid="nav-signup" onClick={() => navigate("/auth?mode=register")} className="bg-emerald-600 hover:bg-emerald-700 rounded-full">
                Join free
              </Button>
            </>
          ) : (
            <>
            <AccountTypeChip role={user.role} />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="nav-user-menu" className="rounded-full">
                  <Menu className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-semibold text-zinc-900">{user.name}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-zinc-500">
                    <span className={`h-1.5 w-1.5 rounded-full ${ACCOUNT_TYPE[user.role]?.dot || "bg-zinc-400"}`} />
                    {ACCOUNT_TYPE[user.role]?.label || user.role} account
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-discover" onClick={() => navigate("/discover")}>
                  <Compass className="mr-2 h-4 w-4" /> Discover creators
                </DropdownMenuItem>
                {user.role !== "admin" && (
                  <DropdownMenuItem data-testid="menu-opportunities" onClick={() => navigate(oppPath)}>
                    <Megaphone className="mr-2 h-4 w-4" /> Opportunities
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                </DropdownMenuItem>
                {user.role === "creator" && (
                  <DropdownMenuItem data-testid="menu-network" onClick={() => navigate("/network")}>
                    <Users className="mr-2 h-4 w-4" /> My Network
                  </DropdownMenuItem>
                )}
                {user.role !== "admin" && (
                  <DropdownMenuItem data-testid="menu-analytics" onClick={() => navigate("/analytics")}>
                    <BarChart3 className="mr-2 h-4 w-4" /> Analytics
                  </DropdownMenuItem>
                )}
                {user.role === "business" && (
                  <DropdownMenuItem data-testid="menu-marketing" onClick={() => navigate("/business/marketing")}>
                    <Wand2 className="mr-2 h-4 w-4" /> AI Marketing
                  </DropdownMenuItem>
                )}
                {user.role !== "admin" && (
                  <DropdownMenuItem data-testid="menu-plans" onClick={() => navigate("/plans")}>
                    <Sparkles className="mr-2 h-4 w-4" /> Plans & pricing
                  </DropdownMenuItem>
                )}
                {user.role !== "admin" && (
                  <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(profilePath)}>
                    <User className="mr-2 h-4 w-4" /> Edit profile
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <Shield className="mr-2 h-4 w-4" /> Admin panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }}>
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function MobileNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  if (!user) return null;

  const items = [
    { icon: Compass, label: "Discover", path: "/discover" },
    ...(user.role !== "admin" ? [{ icon: Megaphone, label: "Opps", path: user.role === "business" ? "/business/opportunities" : "/opportunities" }] : []),
    ...(user.role === "creator" ? [{ icon: Users, label: "Network", path: "/network" }] : []),
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    ...(user.role === "business" ? [{ icon: Wand2, label: "Marketing", path: "/business/marketing" }] : []),
    ...(user.role !== "admin" ? [{ icon: BarChart3, label: "Analytics", path: "/analytics" }] : []),
    user.role === "admin"
      ? { icon: Shield, label: "Admin", path: "/admin" }
      : { icon: User, label: "Profile", path: user.role === "business" ? "/business/profile" : "/creator/profile" },
  ];

  return (
    <div className="nf-mobilenav fixed bottom-0 left-0 right-0 z-50 border-t border-zinc-200 bg-white/95 backdrop-blur-xl md:hidden">
      <div className="flex items-center justify-around px-2 py-1.5">
        {items.map((it) => {
          const active = pathname === it.path || pathname.startsWith(it.path + "/");
          return (
            <button
              key={it.path}
              data-testid={`mobilenav-${it.label.toLowerCase()}`}
              onClick={() => navigate(it.path)}
              className={`flex flex-1 flex-col items-center gap-0.5 rounded-lg py-1.5 text-xs font-medium transition-colors ${
                active ? "text-emerald-600" : "text-zinc-400"
              }`}
            >
              <it.icon className="h-5 w-5" />
              {it.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
